import { useEffect, useRef, useState } from "react";
import type { Card, ShTableState } from "../gen/bazivan/v1/table_pb";
import { signal, useSignal, type Signal } from "../net/signal";
import { HokmScene } from "../hokm/scene";
import { ShelemOnline, type ShOverlay } from "../shelem/controller";
import { cardPoints, MAX_BID, ShPhase, unitOf, type ShelemLocal } from "../shelem/state";
import { Overlay } from "../ui/Overlay";
import { MatchOver } from "./MatchOver";
import { unitName } from "./watching";
import { fa, isRed, suitName, suitSymbol, suits } from "./cards";
import { useDealSheet } from "./DealProof";
import { handOutcome, nextLabel } from "./nextLabel";
import { BreakNext } from "./BreakNext";

/**
 * میزِ شلم — ظرفِ ری‌اکتیِ صحنه‌ی امری.
 *
 * صحنه همان HokmScene است ([[asHokmView]]) و کرومِ دورِ میز این‌جاست. سه پنجره‌اش —
 * **مزایده**، **واگذاری** و **حکم** — کارتِ کوچکِ وسطِ میزند، همان کارتِ حکمِ حکم: دست و
 * حریف‌ها پیدا می‌مانند. کدامشان باز باشد را `c.ask` می‌گوید، که با لمس همان لحظه بسته می‌شود.
 */
export function ShelemTable({ state }: { state: ShTableState }) {
  const host = useRef<HTMLDivElement>(null);
  const sig = useRef<Signal<ShelemOnline | null> | null>(null);
  if (!sig.current) sig.current = signal<ShelemOnline | null>(null);

  useEffect(() => {
    const el = host.current;
    if (!el) return;
    let c: ShelemOnline | null = null;
    const scene = new HokmScene(el, state.seats.length || 4, {
      onCard: (i) => c?.clickCard(i),
      seatName: (l) => c?.seatName(l) ?? "حریف",
    });
    c = new ShelemOnline(scene);
    sig.current!.value = c;
    c.start();
    const ro = new ResizeObserver(() => scene.relayout());
    ro.observe(el);
    return () => { ro.disconnect(); c?.dispose(); scene.destroy(); sig.current!.value = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // تابلو بیرونِ میز و هم‌ردیفِ تابلوی حکم و چهاربرگ (App.tsx)، نه درونِ صحنه: آن‌جا
  // چهره‌ی صندلیِ بالا رویش می‌افتاد و نیمی از امتیاز پنهان می‌شد.
  return (
    <>
      <ShScorebar sig={sig.current} />
      <div className="table tv-host hkm" ref={host}>
        <Chrome sig={sig.current} />
      </div>
    </>
  );
}

function Chrome({ sig }: { sig: Signal<ShelemOnline | null> }) {
  const c = useSignal(sig);
  if (!c) return null;
  return <ChromeInner c={c} />;
}

function ChromeInner({ c }: { c: ShelemOnline }) {
  const overlay = useSignal(c.overlay);
  const proof = useSignal(c.proof);
  const deal = useDealSheet((l) => c.seatName(l));
  const ask = useSignal(c.ask);
  const picked = useSignal(c.picked);
  useSignal(c.raw); // هر وضعیتِ تازه این کروم را هم می‌کشد
  const st = c.state;
  const mine = (u: number) => !st.watching && u === 0;

  return (
    <>
      {/* کلید کفِ مزایده است: خواندنِ تازه‌ی دیگران عددِ پیشنهادی را از نو روی کف می‌گذارد. */}
      {ask === "bid" && <BidPicker key={st.bidFloor} c={c} />}
      {ask === "discard" && <DiscardCard c={c} picked={picked} />}
      {ask === "trump" && <TrumpPicker c={c} />}

      {overlay?.kind === "hand" && (() => {
        const outcome = st.watching ? "even" : handOutcome(overlay.roundScore, mine);
        const r = handResult(overlay);
        return (
          <Overlay label="پایان دست" className="minor" card={outcome}>
            <div className="vt">{handTitle(st, overlay)}</div>
            <div className="muted">
              حاکم: {teamLabel(st, overlay.declUnit)} · خواند {fa(overlay.bid)} · جمع کرد {fa(r.collected)}
            </div>
            <table className="res">
              <thead>
                <tr><th /><th>این دست</th><th>جمع</th></tr>
              </thead>
              <tbody>
                {overlay.roundScore.map((n, u) => (
                  <tr key={u} className={mine(u) ? "me" : ""}>
                    <td>{teamLabel(st, u)}</td>
                    <td className={n < 0 ? "loss" : "gain"} dir="ltr">{n < 0 ? "−" : "+"}{fa(Math.abs(n))}</td>
                    <td dir="ltr">{signed(st.score[u] ?? 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <BreakNext watch={c.brk} label={nextLabel("دست", outcome)} onNext={() => c.nextHand()} />
            {deal.chip(proof)}
          </Overlay>
        );
      })()}

      {overlay?.kind === "match" && (
        <MatchOver score={st.score} me={0} units={2} watching={st.watching} />
      )}
      {deal.sheet}
    </>
  );
}

/** مجموعِ امتیازِ یک دست — RoundPool ِ سرور (rules/shelem). */
const POOL = 165;

/** شلم همیشه دو تیم است: بازیکن «ما» و «حریف» می‌بیند، تماشاچی «تیمِ ۱» و «تیمِ ۲». */
const teamLabel = (st: ShelemLocal, u: number) =>
  st.watching ? unitName(u, true) : u === 0 ? "ما" : "حریف";

/**
 * دستِ تمام‌شده از رویدادِ hand_over: حاکم چه شد و چقدر جمع کرد.
 *
 * رویداد «جمع‌کرده» ندارد، ولی سهمِ حریف همیشه همان جمع‌کرده‌ی خودش است (rules/shelem
 * ScoreHand) و جمعِ دو تیم ۱۶۵؛ پس جمع‌کرده‌ی حاکم از همین درمی‌آید — بی‌آن‌که چشم به
 * وضعیتِ بعدی بماند که پشتِ رویداد در صف است.
 */
export function handResult(o: Extract<ShOverlay, { kind: "hand" }>) {
  const du = o.declUnit, ou = 1 - du;
  const declShelem = o.shelem && (o.roundScore[du] ?? 0) > 0;
  const oppShelem = o.shelem && !declShelem;
  const collected = declShelem ? POOL : oppShelem ? 0 : POOL - (o.roundScore[ou] ?? 0);
  const fate = declShelem ? "shelem" : oppShelem ? "oppShelem" : o.yasa ? "yasa" : o.kot ? "kot" : "made";
  return { fate, collected } as const;
}

/**
 * تیترِ پنلِ دست: چه شد و برای کدام تیم — «کُت شدیم»، «حریف تعهد را برد»، «شلم کردیم!».
 * پیش از این فقط «کُت» بود، بی فاعل و بی عدد: معلوم نبود کُتِ ما بود یا حریف.
 */
export function handTitle(st: ShelemLocal, o: Extract<ShOverlay, { kind: "hand" }>): string {
  const { fate } = handResult(o);
  const du = o.declUnit;
  const sweeper = fate === "oppShelem" ? 1 - du : du;
  if (st.watching) {
    const t = (u: number) => unitName(u, true);
    switch (fate) {
      case "shelem": case "oppShelem": return `${t(sweeper)} شلم کرد!`;
      case "made": return `${t(du)} تعهد را برد`;
      case "kot": return `${t(du)} کُت شد`;
      case "yasa": return `${t(du)} یاسا شد`;
    }
  }
  const us = du === 0;
  switch (fate) {
    case "shelem": case "oppShelem": return sweeper === 0 ? "شلم کردیم!" : "حریف شلم کرد";
    case "made": return us ? "تعهد را بردیم" : "حریف تعهد را برد";
    case "kot": return us ? "کُت شدیم" : "حریف کُت شد";
    case "yasa": return us ? "یاسا شدیم" : "حریف یاسا شد";
  }
}

/** امتیازِ علامت‌دار با منهای واقعی؛ جهتِ عدد را خودِ `dir="ltr"` ِ جایش نگه می‌دارد. */
const signed = (n: number) => (n < 0 ? "−" : "") + fa(Math.abs(n));

const pct = (n: number, of: number) => `${Math.max(0, Math.min(100, (n / Math.max(1, of)) * 100))}%`;

/**
 * تابلوی بالای میزِ شلم — همان خانواده‌ی تابلوی حکم و چهاربرگ.
 *
 * سه تکه: دو ردیفِ تیم با میله‌ی تا هدف (امتیازِ منفی میله‌ی خالی و عددِ سرخ دارد)،
 * کفه‌ی دستِ جاری، و هدف. صحنه وضعیت را با تأخیرِ انیمیشن می‌گیرد، پس تابلو هم از
 * همان کنترلر می‌خواند و نه از وضعیتِ خامِ سرور، تا عدد پیش از نشستنِ برگ نپرد.
 */
function ShScorebar({ sig }: { sig: Signal<ShelemOnline | null> }) {
  const c = useSignal(sig);
  if (!c) return null;
  return <ShScorebarInner c={c} />;
}

function ShScorebarInner({ c }: { c: ShelemOnline }) {
  useSignal(c.raw);
  const st = c.state;
  return (
    <div className="scorebar shboard">
      <div className="rows">
        {[0, 1].map((u) => {
          const v = st.score[u] ?? 0;
          return (
            <div key={u} className={`srow ${u === 0 ? "us" : "them"}`}>
              <span className="who">{teamLabel(st, u)}</span>
              <span className="bar"><i style={{ width: pct(v, st.targetScore) }} /></span>
              {/* کلید عدد را دارد: امتیازِ تازه از نو سوار می‌شود و تکانش یک‌بار اجرا می‌شود */}
              <b key={v} className={v < 0 ? "num neg" : "num"} dir="ltr">{signed(v)}</b>
            </div>
          );
        })}
      </div>
      <ShPact c={c} />
      <div className="match">
        <span className="l">هدف</span>
        <b>{fa(st.targetScore)}</b>
      </div>
    </div>
  );
}

/**
 * کفه‌ی دستِ جاری.
 *
 * سرِ مزایده بالاترین خواندن را می‌گوید — بی این فقط کسی که نوبتش بود عدد را
 * می‌دید. بعد از مزایده، تعهدِ حاکم: «۶۵ از ۱۲۰» و یک میله‌ی ۱۶۵تایی که حاکم از
 * یک سر پر می‌کند و حریف از سرِ دیگر؛ خطِ رویش همان خواندن است. حاکم که از خط
 * بگذرد تعهد رسیده، حریف که از آن بگذرد شکسته — همان قاعده‌ی سرور
 * (made = col ≥ Bid، و جمعِ دو تیم همیشه ۱۶۵).
 */
function ShPact({ c }: { c: ShelemOnline }) {
  const st = c.state;
  if (st.declarer >= 0) {
    const du = unitOf(st.declarer);
    const got = st.collected[du] ?? 0;
    const opp = st.collected[1 - du] ?? 0;
    const fate = got >= st.bid ? " made" : opp > POOL - st.bid ? " broken" : "";
    return (
      <div className={`shpact ${du === 0 ? "us" : "them"}${fate}`} title="تعهد حاکم و امتیازی که تا حالا جمع کرده">
        <span className="l">حاکم: {teamLabel(st, du)}</span>
        <b>{fa(got)}<small> از {fa(st.bid)}</small></b>
        <span className="tug">
          <i className="d" style={{ width: pct(got, POOL) }} />
          <i className="o" style={{ width: pct(opp, POOL) }} />
          <em style={{ insetInlineStart: pct(st.bid, POOL) }} />
        </span>
      </div>
    );
  }
  if (st.phase !== ShPhase.BID) return null;
  const high = st.highSeat >= 0;
  return (
    <div className="shpact bidding">
      <span className="l">مزایده</span>
      <b>{high ? fa(st.highBid) : "—"}</b>
      <span className="l who">{high ? c.seatName(st.highSeat) : "هنوز کسی نخوانده"}</span>
    </div>
  );
}

/**
 * انتخابگرِ مزایده: یک عدد با «+۵» و «−۵»، و یک دکمه‌ی «۱۲۰ بخوان».
 *
 * پیش از این سیزده سکه در یک ردیفِ لغزان بود که روی گوشی از ۱۳۵ به بعدش بیرونِ صفحه می‌ماند،
 * و کلِ نوار پایینِ میز سرِ کارت‌های خودت را می‌پوشاند. حالا کارتِ کوچکِ وسطِ میز است، عدد از کفِ
 * خواندنی شروع می‌شود و دکمه‌ی خواندن همان عدد را می‌گوید — همان انتخابگرِ اپ (ShelemView.OpenBidPicker).
 * «پاس» سرِ اجبارِ شروع‌کننده اصلاً نمی‌آید، چون نبودنش گویاتر از خاموش‌بودنش است.
 */
function BidPicker({ c }: { c: ShelemOnline }) {
  const st = c.state;
  const floor = st.bidFloor;
  const [v, setV] = useState(floor);
  const val = Math.min(MAX_BID, Math.max(floor, v));
  const open = floor <= MAX_BID;
  return (
    <div className="shask shbid" role="group" aria-label="مزایده">
      <div className="head">{st.mustBid ? "شروع‌کننده‌ای — باید بخوانی" : "بخوان یا پاس بده"}</div>
      <div className="muted">
        {st.highSeat >= 0 ? <>بالاترین: {fa(st.highBid)} · {c.seatName(st.highSeat)}</> : "هنوز کسی نخوانده"}
      </div>
      {open ? (
        <div className="stepper">
          <button type="button" className="step" dir="ltr" aria-label="پنج بیشتر" disabled={val >= MAX_BID} onClick={() => setV(val + 5)}>+{fa(5)}</button>
          <b className="val" aria-live="polite">{fa(val)}</b>
          <button type="button" className="step" dir="ltr" aria-label="پنج کمتر" disabled={val <= floor} onClick={() => setV(val - 5)}>−{fa(5)}</button>
        </div>
      ) : (
        <div className="muted">بالاتر از {fa(MAX_BID)} نمی‌شود خواند</div>
      )}
      <div className="acts">
        {open && <button type="button" className="btn go" onClick={() => c.bid(val)}>{fa(val)} بخوان</button>}
        {!st.mustBid && <button type="button" className="btn pass" onClick={() => c.pass()}>پاس</button>}
      </div>
    </div>
  );
}

/**
 * واگذاری: چهار برگ از شانزده.
 *
 * خودِ انتخاب روی دست است — برگِ زده‌شده بالا می‌آید و با زدنِ دوباره پایین می‌رود، و برگ‌های
 * زمین در دست نشانِ «زمین» دارند ([[HokmScene.markDiscard]]). این کارت فقط شمار و تأیید است؛
 * پیش از این رونوشتِ برگ‌ها این‌جا هم می‌نشست و هر برگ دو جا دیده می‌شد. امتیازِ این چهار برگ
 * هم می‌آید، چون برگِ واگذارشده مالِ حاکم حساب می‌شود.
 */
function DiscardCard({ c, picked }: { c: ShelemOnline; picked: Card[] }) {
  const n = picked.length;
  const pts = picked.reduce((a, card) => a + cardPoints(card), 0);
  return (
    <div className="shask shdiscard" role="group" aria-label="واگذاری">
      <div className="head">چهار برگ را بزن تا کنار بگذاری</div>
      <div className="pips" aria-label={`${fa(n)} از ۴`}>
        {[0, 1, 2, 3].map((i) => <i key={i} className={i < n ? "on" : ""} />)}
        <span>{fa(n)} از ۴</span>
      </div>
      <div className="muted">
        {pts > 0 ? `این چهار برگ ${fa(pts)} امتیاز برایتان می‌آورد` : "برگ‌های زمین در دستت نشان دارند"}
      </div>
      <div className="acts">
        <button type="button" className="btn go" disabled={n !== 4} onClick={() => c.discard()}>واگذار کن</button>
      </div>
    </div>
  );
}

/** انتخابگرِ حکم — همان کارتِ وسطِ میزِ حکم ([[HokmTable]] ‎.hktrump)، با همان چهار دکمه. */
function TrumpPicker({ c }: { c: ShelemOnline }) {
  return (
    <div className="shtrump hktrump" role="group" aria-label="حکم را بخوان">
      <div className="head">حکم را بخوان</div>
      <div className="suits">
        {suits.map((s) => (
          <button key={s} type="button" className={`suitbtn ${isRed(s) ? "red" : ""}`} onClick={() => c.callTrump(s)} aria-label={suitName[s]}>
            {suitSymbol[s]}
          </button>
        ))}
      </div>
    </div>
  );
}
